/**
 * Data Provider
 *
 * React context providing the active Repository instance.
 * Phase 1: Always provides LocalRepository.
 * Phase 3+: Will switch based on auth state (local vs Firestore).
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Repository } from '@/data/repository';
import { ForgeRepository } from '@/data/forge-repository';

const DataContext = createContext<Repository | null>(null);

interface DataProviderProps {
  readonly children: ReactNode;
}

/**
 * Provides a Repository instance to the component tree.
 * Phase 3: Uses ForgeRepository for remote data.
 */
export function DataProvider({ children }: DataProviderProps) {
  const repository = useMemo(() => new ForgeRepository(), []);

  return (
    <DataContext.Provider value={repository}>
      {children}
    </DataContext.Provider>
  );
}

/**
 * Hook to access the current Repository instance.
 * Must be used within a DataProvider.
 */
export function useRepository(): Repository {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useRepository must be used within a DataProvider');
  }
  return context;
}
